/** A TEXT's justification, as DXF stores it and as a canvas needs it.
 *
 * ⚠⚠⚠ **THIS WAS WRONG FOR EVERY IMPORTED DRAWING UNTIL 2026-09-07.** DXF group 72 is the
 * horizontal justification and 73 the vertical, and the old canvas path drew every old-style
 * TEXT left-aligned on its baseline. Measured in this office's own imports: **3,831 of 6,735
 * TEXT entities — 56.9% — in 60 of the 77 drawings that hold text.** The commonest single case
 * is halign 1 / valign 2, middle-centre, at 1,900: a title-block cell or a room label.
 *
 * ⚠ Separately, and worse, group 11 (the ALIGNMENT point) is where a justified TEXT actually
 * goes; group 10 is often left at the origin. `display.py` resolves that and sends the right
 * point — see the `get_placement()` note there.
 *
 * ⚠ Pure arithmetic in its own file so it can be tested without pulling React in through
 * `Canvas2D`. The ENCODE half lives in `display.py`; `textjust.test.ts` states the contract both
 * sides have to agree on, which is the thing that actually breaks.
 */

/** DXF group 72/73 packed into the display row's `att` slot.
 *  ⚠⚠ **0 MEANS LEFT-AND-BASELINE AND MUST GO ON MEANING THAT.** A display list written before
 *  FORMAT 33 has 0 in every TEXT row; an encoding whose default was non-zero would make every
 *  cached drawing move its text the moment this shipped. */
export function packJustify(halign: number, valign: number): number {
  return (halign || valign) ? halign * 4 + valign + 1 : 0;
}

/** ⚠ ALIGNED (3) and FIT (5) span TWO points and are the only justifications that do. Named so
 *  the canvas, `display.py` and `edits.py` cannot each keep their own list and forget one. */
export const TWO_POINT = new Set([3, 5]);

/** The horizontal justification packed into `att`, or 0. */
export function halignOf(att: number): number {
  return att ? Math.floor((att - 1) / 4) : 0;
}

export interface TextAnchor {
  align: CanvasTextAlign;
  baseline: CanvasTextBaseline;
}

/** What the canvas should be set to for a packed `att`.
 *
 * ⚠⚠ **halign 4 IS `MIDDLE`, WHICH IS CENTRED BOTH WAYS AND OVERRIDES valign.** AutoCAD treats
 * it as its own justification rather than as "centre horizontally"; reading it as the latter
 * puts a tag half a character-height out, which looks like a font problem and is not.
 * ⚠ halign 3 (ALIGNED) and 5 (FIT) span TWO points and scale the glyphs to suit. Neither is
 * implemented; `display.py` sends the midpoint of the two ends, so centring them is the closest
 * honest thing. Neither occurs anywhere in this office's corpus.
 */
export function anchorOf(att: number): TextAnchor {
  if (!att) return { align: "left", baseline: "alphabetic" };
  const h = Math.floor((att - 1) / 4);
  const v = (att - 1) % 4;
  return {
    align: h === 0 ? "left" : h === 2 ? "right" : "center",
    baseline: h === 4 ? "middle"
            : v === 1 ? "bottom" : v === 2 ? "middle" : v === 3 ? "top" : "alphabetic",
  };
}
